import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
  Button,
  HStack,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card.js';
import { getData, postData } from 'utils/httpClient';
import {
  THEORY_ABSENCE_LIMIT,
  PRACTIC_ABSENCE_LIMIT,
} from 'utils/absenceRules';

/*
  Vista INASISTENCIAS del dictado :id.

  Reglas de negocio (definidas por cliente):
    - Teórico:  máximo 20% de inasistencias.
    - Práctico: máximo 10% de inasistencias.
    - Justificada = media falta.
    - Si excede → recursa.

  Implementación actual:
    - Listado de inscriptos: GET /course-inscriptions/course/:id
      (trae los student_course activos del dictado con persona del alumno).
    - Registrar falta: POST /course-attendance con el shape real del backend
      { studentId, courseId, attendanceDate, status, notes, createdBy }.
    - Status:
        JUSTIFIED  → cuando el checkbox "justificada" está tildado (½ falta).
        ABSENT     → falta injustificada (falta completa).
      El backend calcula la tasa considerando JUSTIFIED como presencia parcial.
    - El tipo de clase (TEÓRICO/PRÁCTICO) no está modelado en course_attendance;
      se persiste como prefijo en notes para no perder el dato.
      TODO: cuando se agregue al modelo, mover a una columna dedicada.
*/
export default function DictatedCourseAbsencesView() {
  const { id } = useParams();
  const [students, setStudents] = useState([]);
  const [date, setDate] = useState('');
  const [classType, setClassType] = useState('TEORICO');
  const [justification, setJustification] = useState({});
  const [submittingId, setSubmittingId] = useState(null);
  const toast = useToast();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getData(`/course-inscriptions/course/${id}`);
        setStudents(res?.payload?.inscriptions || []);
      } catch (error) {
        toast({
          title: 'Error al obtener inscriptos',
          description: error?.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    };
    if (id) load();
  }, [id, toast]);

  const markAbsence = async (inscription) => {
    const studentId = inscription?.student?.id;
    if (!studentId) {
      toast({
        title: 'No se pudo determinar el estudiante',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      setSubmittingId(inscription.id);
      const isJustified = !!justification[inscription.id];
      await postData('/course-attendance', {
        studentId,
        courseId: Number(id),
        attendanceDate: date,
        status: isJustified ? 'JUSTIFIED' : 'ABSENT',
        notes: `${classType}${isJustified ? ' | JUSTIFICADA' : ''}`,
        createdBy: 1,
      });
      toast({
        title: isJustified ? 'Falta justificada registrada' : 'Falta registrada',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'No se pudo registrar la falta',
        description: error?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card p={5}>
        <Heading size="md" mb={4}>
          Inasistencias - Dictado #{id}
        </Heading>
        <Text fontSize="sm" color="gray.500" mb={3}>
          Tope: Teórico {THEORY_ABSENCE_LIMIT * 100}% · Práctico{' '}
          {PRACTIC_ABSENCE_LIMIT * 100}%. Justificada = ½ falta. Si se supera
          el tope, el alumno recursa.
        </Text>
        <HStack mb={4} spacing={3}>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            maxW="200px"
          />
          <Select
            value={classType}
            onChange={(e) => setClassType(e.target.value)}
            maxW="200px"
          >
            <option value="TEORICO">Teórico</option>
            <option value="PRACTICO">Práctico</option>
          </Select>
        </HStack>

        {students.length === 0 ? (
          <Text>No hay inscriptos en este dictado.</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Estudiante</Th>
                <Th>Justificada (½ falta)</Th>
                <Th>Acción</Th>
              </Tr>
            </Thead>
            <Tbody>
              {students.map((ins) => (
                <Tr key={ins.id}>
                  <Td>
                    {ins?.student?.person?.name}{' '}
                    {ins?.student?.person?.lastName}
                  </Td>
                  <Td>
                    <input
                      type="checkbox"
                      checked={!!justification[ins.id]}
                      onChange={(e) =>
                        setJustification((p) => ({
                          ...p,
                          [ins.id]: e.target.checked,
                        }))
                      }
                    />
                  </Td>
                  <Td>
                    <Button
                      size="xs"
                      colorScheme="red"
                      onClick={() => markAbsence(ins)}
                      isDisabled={!date || submittingId === ins.id}
                    >
                      Marcar falta
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Card>
    </Box>
  );
}
